import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNotify, useRefresh } from 'react-admin';
import { PDFFormEditor, usePDFEditor } from '../components/PDFFormEditor';
import { getToken, getRefreshToken } from '../authentication/authProvider';
import OasCancellationSendModal from './OasCancellationSendModal';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

// Encode a Uint8Array as base64 in chunks so very large PDFs don't blow the
// argument-length cap of String.fromCharCode.apply. (Same helper as
// SurvivorBenefitsButton.)
const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + chunkSize)),
    );
  }
  return btoa(binary);
};

interface OasCancellationModalProps {
  open: boolean;
  onClose: () => void;
  funeralId: number;
  /** Used to build the signing link for "Copy link" in the send modal. */
  funeralUuid?: string;
  /** Whether the form has already been confirmed (drives the Ready button state). */
  confirmed?: boolean;
  /** Used only for the downloaded file name. */
  deceasedName?: string;
  /** Edition to open: the one saved at Ready, else the family's language. */
  defaultLanguage?: 'en' | 'fr';
}

// Preview + send the "Cancel OAS" (ISP-1201) Notification of Death form. Mirrors
// the QPP single-form modal in SurvivorBenefitsButton: PDFFormEditor preview,
// a Ready button that persists overrides + the flattened bytes, a Download
// button, and a Send button that kicks off the standalone DocuSeal signing
// flow. Available for every funeral (any region). Service Canada publishes the
// form in English and French; the EN/FR switch picks the edition, and that
// choice is what Ready saves and Send prints.
const OasCancellationModal: React.FC<OasCancellationModalProps> = ({
  open,
  onClose,
  funeralId,
  funeralUuid,
  confirmed = false,
  deceasedName,
  defaultLanguage = 'en',
}) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [language, setLanguage] = useState<'en' | 'fr'>(defaultLanguage);

  const {
    handlePdfReady,
    downloadFlattenedPdf,
    getFlattenedPdfBytes,
    getFieldValues,
  } = usePDFEditor();

  const pdfUrl = `${PAPERMAN_API_URL}cpp-benefits/oas-cancellation-form/${funeralId}?language=${language}`;

  // Switching edition reloads the form from the case (+ saved overrides);
  // unsaved edits on screen are dropped.
  const handleLanguageChange = (_: unknown, next: 'en' | 'fr' | null) => {
    if (!next || next === language) return;
    setLoading(true);
    setLanguage(next);
  };

  const handlePdfLoaded = useCallback(
    (bytes: Uint8Array) => {
      setLoading(false);
      handlePdfReady(bytes);
    },
    [handlePdfReady],
  );

  const fileName = useCallback(() => {
    const safe = (deceasedName || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
    return `OAS_Cancellation_${safe || funeralId}${language === 'fr' ? '_FR' : ''}.pdf`;
  }, [deceasedName, funeralId, language]);

  // Ready: persist the agent's edits as overrides, then upload the flattened
  // bytes so the signing service sends exactly what the agent saw.
  const handleReady = async () => {
    const overrides = getFieldValues();
    if (!overrides) {
      notify(
        'PDF not loaded yet — wait for the preview before marking ready',
        { type: 'warning' },
      );
      return;
    }
    setConfirming(true);
    try {
      const token = await getToken();
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Refresh-Token': await getRefreshToken(),
      };
      const confirmRes = await fetch(
        `${PAPERMAN_API_URL}form-confirmations/${funeralId}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ formKey: 'oas_cancellation', overrides, language }),
        },
      );
      if (!confirmRes.ok) {
        const errBody = await confirmRes.json().catch(() => ({}));
        throw new Error(errBody?.message || `Confirm failed (${confirmRes.status})`);
      }

      const bytes = await getFlattenedPdfBytes();
      const pdfBase64 = bytesToBase64(bytes);
      const saveRes = await fetch(
        `${PAPERMAN_API_URL}cpp-benefits/save-flattened/${funeralId}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ formKey: 'oas_cancellation', pdfBase64 }),
        },
      );
      if (!saveRes.ok) {
        const errBody = await saveRes.json().catch(() => ({}));
        throw new Error(errBody?.message || `Save PDF failed (${saveRes.status})`);
      }

      notify('OAS cancellation form marked as ready', { type: 'success' });
      refresh();
    } catch (err: any) {
      console.error('OAS confirm error:', err);
      notify(err?.message || 'Failed to mark form as ready', { type: 'error' });
    } finally {
      setConfirming(false);
    }
  };

  const handleDownload = async () => {
    try {
      await downloadFlattenedPdf(fileName());
      notify('OAS cancellation form downloaded', { type: 'success' });
    } catch (err) {
      console.error('OAS download error:', err);
      notify('Failed to download OAS cancellation form', { type: 'error' });
    }
  };

  // Send: open the review-and-send modal so the agent sees the recipient and
  // the draft email before anything goes out.
  const handleSend = () => setSendModalOpen(true);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle>Cancel OAS — Notification of Death (CPP/OAS)</DialogTitle>
      <DialogContent
        sx={{ flexGrow: 1, p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            p: 1,
          }}
        >
          <ToggleButtonGroup
            value={language}
            exclusive
            size="small"
            onChange={handleLanguageChange}
            disabled={confirming}
            sx={{ mr: 'auto' }}
          >
            <ToggleButton value="en">English</ToggleButton>
            <ToggleButton value="fr">Français</ToggleButton>
          </ToggleButtonGroup>
          <Button
            onClick={handleReady}
            variant={confirmed ? 'outlined' : 'contained'}
            color="success"
            size="small"
            disabled={loading || confirming}
            startIcon={
              confirming ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />
            }
          >
            Ready
          </Button>
          <Button
            onClick={handleDownload}
            variant="outlined"
            color="primary"
            size="small"
            disabled={loading}
            startIcon={<DownloadIcon />}
          >
            Download
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            color="primary"
            size="small"
            disabled={loading}
            startIcon={<SendIcon />}
          >
            Send
          </Button>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              display: loading ? 'none' : 'block',
              height: '100%',
            }}
          >
            <PDFFormEditor key={language} pdfUrl={pdfUrl} onPdfReady={handlePdfLoaded} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <OasCancellationSendModal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        funeralId={funeralId}
        funeralUuid={funeralUuid}
        formLanguage={language}
        onSent={() => {
          setSendModalOpen(false);
          onClose();
        }}
      />
    </Dialog>
  );
};

export default OasCancellationModal;
